import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Chip,
  Divider,
  Alert,
  CircularProgress,
  Stack,
} from '@mui/material';
import { ArrowBack, Send, ThumbUp, ThumbDown, CheckCircle } from '@mui/icons-material';
import { doc, updateDoc, onSnapshot, getDoc, Timestamp } from 'firebase/firestore';
import { format } from 'date-fns';
import { ko } from 'date-fns/locale';
import { MainLayout } from '../components/layout/MainLayout';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../lib/firebase';
import { Suggestion, getStatusDisplayName, getStatusColor } from '../types/suggestion';

interface LocationState {
  from?: string;
  page?: number;
  filter?: string;
}

export const SuggestionDetailPage: React.FC = () => {
  const { suggestionId } = useParams<{ suggestionId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  const locationState = location.state as LocationState | null;

  const [suggestion, setSuggestion] = useState<Suggestion | null>(null);
  const [loading, setLoading] = useState(true);
  const [replyText, setReplyText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [repliedByName, setRepliedByName] = useState<string>('');
  const [isEditing, setIsEditing] = useState(false);

  // Real-time suggestion subscription
  useEffect(() => {
    if (!suggestionId) return;

    const unsubscribe = onSnapshot(
      doc(db, 'suggestions', suggestionId),
      (docSnapshot) => {
        if (docSnapshot.exists()) {
          const data = { id: docSnapshot.id, ...docSnapshot.data() } as Suggestion;
          setSuggestion(data);

          // Load existing reply
          if (data.reply) {
            setReplyText(data.reply);
          }
        } else {
          setError('제안사항을 찾을 수 없습니다.');
        }
        setLoading(false);
      },
      (err) => {
        console.error('Error fetching suggestion:', err);
        setError('제안사항 정보를 불러오는데 실패했습니다.');
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [suggestionId]);

  // Fetch replied admin name
  useEffect(() => {
    const fetchRepliedByName = async () => {
      if (!suggestion?.repliedBy) {
        setRepliedByName('');
        return;
      }

      try {
        const userDoc = await getDoc(doc(db, 'user_profiles', suggestion.repliedBy));
        let displayName = suggestion.repliedBy;

        if (userDoc.exists()) {
          const userData = userDoc.data();
          const baseName =
            userData.name ||
            userData.organization_name ||
            userData.email ||
            userData.displayName ||
            suggestion.repliedBy;
          displayName = baseName !== suggestion.repliedBy
            ? `${baseName} (${suggestion.repliedBy})`
            : suggestion.repliedBy;
        }

        setRepliedByName(displayName);
      } catch (error) {
        console.error('[답변 작성자] 조회 오류:', error);
        setRepliedByName(suggestion.repliedBy);
      }
    };

    fetchRepliedByName();
  }, [suggestion?.repliedBy]);

  // Navigate back with state preservation
  const handleBackToList = () => {
    if (locationState?.page || locationState?.filter) {
      navigate('/suggestions', {
        state: {
          page: locationState.page,
          filter: locationState.filter,
        },
      });
    } else {
      navigate('/suggestions');
    }
  };

  // Submit or update reply
  const handleSubmitReply = async () => {
    if (!suggestion || !user || !suggestionId) return;

    if (!replyText.trim()) {
      setError('답변 내용을 입력해주세요.');
      return;
    }

    setSubmitting(true);
    setError('');
    setSuccess('');

    try {
      await updateDoc(doc(db, 'suggestions', suggestionId), {
        reply: replyText.trim(),
        repliedAt: Timestamp.now(),
        repliedBy: user.uid,
      });

      const message = suggestion.reply ? '답변이 성공적으로 수정되었습니다.' : '답변이 성공적으로 등록되었습니다.';
      setSuccess(message);
      setIsEditing(false);

      // Show success message and navigate back after 2 seconds
      setTimeout(() => {
        handleBackToList();
      }, 2000);
    } catch (err: any) {
      console.error('Error submitting reply:', err);
      setError('답변 등록에 실패했습니다. 다시 시도해주세요.');
    } finally {
      setSubmitting(false);
    }
  };

  // Delete reply
  const handleDeleteReply = async () => {
    if (!suggestion || !user || !suggestionId) return;

    if (!window.confirm('답변을 삭제하시겠습니까?')) {
      return;
    }

    setDeleting(true);
    setError('');
    setSuccess('');

    try {
      await updateDoc(doc(db, 'suggestions', suggestionId), {
        reply: null,
        repliedAt: null,
        repliedBy: null,
      });

      setSuccess('답변이 성공적으로 삭제되었습니다.');
      setReplyText('');
      setIsEditing(false);

      // Show success message briefly
      setTimeout(() => {
        setSuccess('');
      }, 2000);
    } catch (err: any) {
      console.error('Error deleting reply:', err);
      setError('답변 삭제에 실패했습니다. 다시 시도해주세요.');
    } finally {
      setDeleting(false);
    }
  };

  // Enable editing mode
  const handleEditReply = () => {
    setIsEditing(true);
    setError('');
    setSuccess('');
  };

  // Cancel editing
  const handleCancelEdit = () => {
    setIsEditing(false);
    setError('');
    // Restore original reply text
    if (suggestion?.reply) {
      setReplyText(suggestion.reply);
    }
  };

  if (loading) {
    return (
      <MainLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <CircularProgress />
        </Box>
      </MainLayout>
    );
  }

  if (!suggestion) {
    return (
      <MainLayout>
        <Box>
          <Alert severity="error">{error || '제안사항을 찾을 수 없습니다.'}</Alert>
          <Button
            startIcon={<ArrowBack />}
            onClick={handleBackToList}
            sx={{ mt: 2 }}
          >
            목록으로 돌아가기
          </Button>
        </Box>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <Box>
        {/* Back Button */}
        <Button
          startIcon={<ArrowBack />}
          onClick={handleBackToList}
          sx={{ mb: 3 }}
        >
          목록으로 돌아가기
        </Button>

        {/* Suggestion Content */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Typography variant="h5" fontWeight={700}>
              {suggestion.title}
            </Typography>
            <Chip
              label={getStatusDisplayName(suggestion.status)}
              color={getStatusColor(suggestion.status)}
              size="small"
            />
            {suggestion.reply && (
              <Chip
                icon={<CheckCircle />}
                label="답변완료"
                color="success"
                size="small"
              />
            )}
          </Box>

          <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
            <Chip
              label={suggestion.category}
              size="small"
              variant="outlined"
            />
            <Chip
              label={suggestion.userOrganization || '미지정'}
              size="small"
              variant="outlined"
            />
          </Stack>

          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary">
              작성자 소속: {suggestion.userOrganization || '미지정'}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
              작성일:{' '}
              {suggestion.createdAt
                ? format(suggestion.createdAt.toDate(), 'yyyy년 MM월 dd일 HH:mm', {
                    locale: ko,
                  })
                : '-'}
            </Typography>
          </Box>

          <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <ThumbUp sx={{ fontSize: 16, color: '#10b981' }} />
              <Typography variant="caption" fontWeight={600}>
                찬성 {suggestion.upvotes}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <ThumbDown sx={{ fontSize: 16, color: '#ef4444' }} />
              <Typography variant="caption" fontWeight={600}>
                반대 {suggestion.downvotes}
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />

          <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.8 }}>
            {suggestion.content}
          </Typography>
        </Paper>

        {/* Reply Section */}
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" fontWeight={600} gutterBottom>
            답변 작성
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}

          <TextField
            fullWidth
            multiline
            rows={8}
            placeholder="답변 내용을 입력하세요..."
            value={replyText}
            onChange={(e) => setReplyText(e.target.value)}
            disabled={submitting || deleting || (!!suggestion.reply && !isEditing)}
            sx={{ mb: 2 }}
          />

          {suggestion.reply && suggestion.repliedAt && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.secondary">
                답변 등록일:{' '}
                {format(suggestion.repliedAt.toDate(), 'yyyy년 MM월 dd일 HH:mm', {
                  locale: ko,
                })}
              </Typography>
              {repliedByName && (
                <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
                  답변 작성자: {repliedByName}
                </Typography>
              )}
            </Box>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            {suggestion.reply && !isEditing ? (
              <>
                <Button
                  variant="outlined"
                  onClick={handleEditReply}
                  disabled={submitting || deleting}
                  size="large"
                >
                  답변 수정
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  onClick={handleDeleteReply}
                  disabled={submitting || deleting}
                  size="large"
                >
                  {deleting ? <CircularProgress size={20} /> : '답변 삭제'}
                </Button>
              </>
            ) : (
              <>
                {isEditing && (
                  <Button
                    variant="outlined"
                    onClick={handleCancelEdit}
                    disabled={submitting || deleting}
                    size="large"
                  >
                    취소
                  </Button>
                )}
                <Button
                  variant="contained"
                  startIcon={submitting ? <CircularProgress size={20} /> : <Send />}
                  onClick={handleSubmitReply}
                  disabled={submitting || deleting || !replyText.trim()}
                  size="large"
                >
                  {suggestion.reply ? '수정 완료' : '답변 등록'}
                </Button>
              </>
            )}
          </Box>
        </Paper>
      </Box>
    </MainLayout>
  );
};
